import { useEffect, useMemo, useRef, useState } from 'react';

import { SearchUtils } from '@spotify/confidence-core-react';
import { UserFragment } from '@spotify/confidence-graphql';
import { useUsers } from '@spotify/plugin-confidence-iam-react';

const DEBOUNCE_MS = 300;

export const useUserSuggestions = (query: string | undefined) => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const filter =
    debouncedQuery !== undefined && debouncedQuery.length > 0
      ? `(email:*${SearchUtils.escapeQuery(debouncedQuery)}*) OR (fullName:*${SearchUtils.escapeQuery(debouncedQuery)}*)`
      : undefined;

  const { users: fetchedUsers } = useUsers({
    filter,
    pageSize: 25,
    orderBy: 'fullName',
    skip: debouncedQuery === undefined,
  });

  // Keep a cache of all seen users so replaceEmailsWithMentions and
  // assignee lookup still work after the mention popup closes.
  const seenUsersRef = useRef(new Map<string, UserFragment>());

  useEffect(() => {
    fetchedUsers?.forEach(user => {
      seenUsersRef.current.set(user.email, user);
    });
  }, [fetchedUsers]);

  const allUsers = Array.from(seenUsersRef.current.values());

  const suggestions = useMemo(
    () =>
      (fetchedUsers ?? []).map(user => ({
        email: user.email,
        name: user.fullName,
        avatarUri: user.pictureUri ?? undefined,
      })),
    [fetchedUsers],
  );

  return { users: allUsers, suggestions };
};
